import ApiInstance from '@/middlewares/api-interceptore';
import { useAuth } from '@/zustand/auth-store';
import { ROUTES } from './routes';

const login = async payload => {
  const { data } = await ApiInstance.post(ROUTES.LOGIN, payload, {
    skipRefresh: true,
    skipRedirect: true,
  });
  return data;
};

const verifyLoginOtp = async payload => {
  const { data } = await ApiInstance.post(ROUTES.VERIFY_LOGIN_OTP, payload);
  return data;
};

const resendLoginOtp = async payload => {
  const { data } = await ApiInstance.post(ROUTES.RESEND_LOGIN_OTP, payload);
  return data;
};
const resendLink = async payload => {
  const { data } = await ApiInstance.post(ROUTES.RESEND_LINK, payload);
  return data;
};
const refreshAccessToken = async () => {
  const { refreshToken } = useAuth.getState();
  const { data } = await ApiInstance.post(
    ROUTES.REFRESH_TOKEN,
    { refreshToken },
    { skipRefresh: true }
  );
  return data?.token || data;
};

const logout = async () => {
  const refreshToken = useAuth.getState().refreshToken;

  if (!refreshToken) {
    throw new Error('No refresh token found');
  }

  const { data } = await ApiInstance.post(ROUTES.LOGOUT, {
    refreshToken,
  });
  return data;
};

const dashboardLogin = async body => {
  const { data } = await ApiInstance.post(ROUTES.DASHBOARD_LOGIN, body);
  return data;
};
const dashboardVerifyOtp = async body => {
  const { data } = await ApiInstance.post(ROUTES.DASHBOARD_VERIFY_OTP, body);
  return data;
};
const dashboardResendOtp = async body => {
  const { data } = await ApiInstance.post(ROUTES.DASHBOARD_RESEND_OTP, body);
  return data;
};

const dashboardRefreshAccessToken = async () => {
  const { refreshToken } = useAuth.getState();
  const { data } = await ApiInstance.post(
    ROUTES.DASHBOARD_REFRESH_TOKEN,
    { refreshToken },
    { skipRefresh: true }
  );
  return data?.token || data;
};

/**
 * Exchange magic-link token (from URL ?token=...) for access token + user.
 * GET /auth/magic-link/:token
 * Response: { message, token: { accessToken, refreshToken }, data: user }
 */
const getMagicLink = async token => {
  const { data } = await ApiInstance.get(`${ROUTES.MAGIC_LINK}/${encodeURIComponent(token)}`, {
    skipRefresh: true,
    skipRedirect: true,
  });
  return data;
};
const otpRequest = async body => {
  const { data } = await ApiInstance.post(ROUTES.REQUEST_OTP, body);
  return data;
};
/**
 * POST /auth/select-account - finishes a general sign in for a person with several usable
 * accounts. Authorized by the 5-minute selection token (not an access token).
 * Body: { membershipId } (null = super admin's platform account).
 * Response: the same { message, token, data } shape as a signed-in magic-link redeem.
 */
const selectAccount = async ({ selectionToken, membershipId }) => {
  const { data } = await ApiInstance.post(
    ROUTES.SELECT_ACCOUNT,
    { membershipId },
    {
      headers: { Authorization: `Bearer ${selectionToken}` },
      skipAuthHeader: true,
      skipRefresh: true,
      skipRedirect: true,
    }
  );
  return data;
};

/**
 * GET /auth/accounts - the signed-in person's usable accounts (the current one included):
 * [{ membershipId, clinicId, clinicName, clinicLogoUrl, kind, role, status }].
 */
const getAccounts = async () => {
  // skipRedirect: the switcher is optional chrome - a failure must not bounce the page.
  const { data } = await ApiInstance.get(ROUTES.ACCOUNTS, { skipRedirect: true });
  return data?.data ?? [];
};

/**
 * POST /auth/switch-account - moves to another account of the same person without a new
 * WhatsApp sign in. Body: { membershipId }. Response: { message, token, data }; the calling
 * session is revoked.
 */
const switchAccount = async ({ membershipId }) => {
  const { data } = await ApiInstance.post(
    ROUTES.SWITCH_ACCOUNT,
    { membershipId },
    { skipRedirect: true }
  );
  return data;
};

export const AuthApis = {
  login,
  verifyLoginOtp,
  resendLoginOtp,
  logout,
  refreshAccessToken,
  dashboardRefreshAccessToken,
  dashboardResendOtp,
  dashboardVerifyOtp,
  dashboardLogin,
  getMagicLink,
  otpRequest,
  resendLink,
  selectAccount,
  getAccounts,
  switchAccount,
};
